import React from 'react';
import { useSystemData } from '../contexts/SystemContext';
import { Server, Database, Activity, RefreshCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const SystemStatus: React.FC = () => {
  const { 
    systemStatus, 
    healthStatus, 
    notionStatus, 
    isLoading, 
    refreshData 
  } = useSystemData();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'running':
      case 'connected':
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'running':
      case 'connected':
      case 'success':
        return 'text-green-600';
      case 'error':
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-yellow-600';
    }
  };

  const formatUptime = (uptime: number) => {
    const seconds = Math.floor(uptime);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  };

  if (isLoading && !systemStatus) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">System Status</h1>
            <p className="text-gray-600">Monitor system health and service connections</p>
          </div>
          <button
            onClick={refreshData}
            disabled={isLoading}
            className="bg-white shadow-sm hover:shadow-md px-4 py-2 rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-200 flex items-center space-x-2 text-gray-700 hover:text-gray-900"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="font-medium">Refresh</span>
          </button>
        </div>
      </div>

      {/* Overall System Health */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">System Health Overview</h2>
          <div className="flex items-center space-x-2">
            {getStatusIcon(notionStatus?.status || 'unknown')}
            <span className={`text-sm font-medium ${getStatusColor(notionStatus?.status || 'unknown')}`}>
              All Systems Operational
            </span>
          </div>
        </div>
        
        {/* Notion Connection Status */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Database className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Notion Database</h3>
                <p className="text-sm text-gray-600">External service connection</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2 mb-1">
                {getStatusIcon(notionStatus?.status || 'unknown')}
                <span className={`font-semibold ${getStatusColor(notionStatus?.status || 'unknown')}`}>
                  {notionStatus?.status || 'Unknown'}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {notionStatus?.prompt_count || 0} prompts loaded
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Metrics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">System Metrics</h3>
            <Server className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Status:</span>
              <div className="flex items-center space-x-2">
                {getStatusIcon(systemStatus?.system.status || 'unknown')}
                <span className={`text-sm font-medium ${getStatusColor(systemStatus?.system.status || 'unknown')}`}>
                  {systemStatus?.system.status || 'Unknown'}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Uptime:</span>
              <span className="text-sm font-medium">
                {systemStatus?.system.uptime ? formatUptime(systemStatus.system.uptime) : 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Memory Usage:</span>
              <span className="text-sm font-medium">
                {systemStatus?.system.memory_usage || 0} %
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active Sessions:</span>
              <span className="text-sm font-medium">
                {systemStatus?.system.active_sessions || 0}
              </span>
            </div>
          </div>
        </div>

        {/* API Health */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">API Health</h3>
            <Activity className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Service:</span>
              <span className="text-sm font-medium">
                {healthStatus?.service || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Status:</span>
              <div className="flex items-center space-x-2">
                {getStatusIcon(healthStatus?.status || 'unknown')}
                <span className={`text-sm font-medium ${getStatusColor(healthStatus?.status || 'unknown')}`}>
                  {healthStatus?.status || 'Unknown'}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Message:</span>
              <span className="text-sm font-medium">
                {healthStatus?.message || 'No message'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Timestamp:</span>
              <span className="text-sm font-medium">
                {healthStatus?.timestamp ? new Date(healthStatus.timestamp * 1000).toLocaleString() : 'Unknown'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemStatus; 